import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ShoppingCart, User, Search, Heart, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Input } from "@/components/ui/input";
import { LanguageToggle } from "./LanguageToggle";
import { useLanguage } from "@/contexts/LanguageContext";
import { Logo } from "./branding/Logo";
import { useWishlist } from "@/hooks/useWishlist";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { AuthModal } from "./auth/AuthModal";
import { CartDrawer } from "./cart/CartDrawer";

const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { wishlistCount } = useWishlist();
  const { user, isAuthenticated } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();

  const handleUserIconClick = () => {
    if (isAuthenticated) {
      navigate("/profile");
    } else {
      setAuthModalOpen(true);
    }
  };

  const shopCategories = [
    { name: t('nav.categories.tshirts'), href: "#" },
    { name: t('nav.categories.hoodies'), href: "#" },
    { name: t('nav.categories.mugs'), href: "#" },
    { name: t('nav.categories.posters'), href: "#" },
    { name: t('nav.categories.phoneCases'), href: "#" },
    { name: t('nav.categories.stickers'), href: "#" },
  ];

  const partners = [
    { name: t('nav.partnerLinks.login'), href: "#" },
    { name: t('nav.partnerLinks.become'), href: "#" },
    { name: t('nav.partnerLinks.directory'), href: "#" },
  ];

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/90">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <Logo size="sm" className="transition-transform hover:scale-105" />
          </Link>

          {/* Desktop Navigation */}
          <div className={`hidden md:flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-6`}>
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent">{t('nav.shop')}</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 bg-popover">
                      {shopCategories.map((category) => (
                        <li key={category.name}>
                          <NavigationMenuLink asChild>
                            <a
                              href={category.href}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none">{category.name}</div>
                            </a>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent">{t('nav.partners')}</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[300px] gap-3 p-4 bg-popover">
                      {partners.map((partner) => (
                        <li key={partner.name}>
                          <NavigationMenuLink asChild>
                            <a
                              href={partner.href}
                              className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                            >
                              <div className="text-sm font-medium leading-none">{partner.name}</div>
                            </a>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link to="/design-studio" className="text-sm font-medium hover:text-primary transition-colors flex items-center gap-1">
              <Sparkles className="h-4 w-4" />
              Design Studio
            </Link>
            <Link to="/sale" className="text-sm font-medium hover:text-primary transition-colors">
              {t('nav.sale')}
            </Link>
            <Link to="/contact" className="text-sm font-medium hover:text-primary transition-colors">
              {t('nav.contact')}
            </Link>
          </div>

          {/* Search and Actions */}
          <div className={`hidden md:flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-4`}>
            <div className="relative">
              <Search className={`absolute ${isRTL ? 'right-2' : 'left-2'} top-2.5 h-4 w-4 text-muted-foreground`} />
              <Input
                placeholder={t('nav.search')}
                className={`${isRTL ? 'pr-8' : 'pl-8'} w-[200px] lg:w-[300px]`}
              />
            </div>
            <LanguageToggle />
            <Button variant="ghost" size="icon" onClick={handleUserIconClick} title={isAuthenticated ? "Profile" : "Login"}>
              <User className="h-5 w-5" />
            </Button>
            <Link to="/wishlist">
              <Button variant="ghost" size="icon" className="relative" title="Wishlist">
                <Heart className="h-5 w-5" />
                {wishlistCount > 0 && (
                  <span className={`absolute -top-1 ${isRTL ? '-left-1' : '-right-1'} h-4 w-4 rounded-full bg-red-600 text-[10px] font-bold text-white flex items-center justify-center`}>
                    {wishlistCount}
                  </span>
                )}
              </Button>
            </Link>
            <Button variant="ghost" size="icon" className="relative" onClick={() => setCartDrawerOpen(true)} title="Shopping Cart">
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className={`absolute -top-1 ${isRTL ? '-left-1' : '-right-1'} h-4 w-4 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center`}>
                  {cartCount}
                </span>
              )}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 space-y-4 border-t">
            <div className="relative">
              <Search className={`absolute ${isRTL ? 'right-2' : 'left-2'} top-2.5 h-4 w-4 text-muted-foreground`} />
              <Input placeholder={t('nav.search')} className={`${isRTL ? 'pr-8' : 'pl-8'} w-full`} />
            </div>

            <div className="space-y-2">
              <details className="group">
                <summary className="font-medium cursor-pointer list-none flex items-center justify-between py-2">
                  {t('nav.shop')}
                  <span className="transition group-open:rotate-180">▼</span>
                </summary>
                <div className="pl-4 space-y-2 mt-2">
                  {shopCategories.map((category) => (
                    <a
                      key={category.name}
                      href={category.href}
                      className="block py-1 text-sm text-muted-foreground hover:text-foreground"
                    >
                      {category.name}
                    </a>
                  ))}
                </div>
              </details>

              <details className="group">
                <summary className="font-medium cursor-pointer list-none flex items-center justify-between py-2">
                  {t('nav.partners')}
                  <span className="transition group-open:rotate-180">▼</span>
                </summary>
                <div className="pl-4 space-y-2 mt-2">
                  {partners.map((partner) => (
                    <a
                      key={partner.name}
                      href={partner.href}
                      className="block py-1 text-sm text-muted-foreground hover:text-foreground"
                    >
                      {partner.name}
                    </a>
                  ))}
                </div>
              </details>

              <Link to="/design-studio" className="block py-2 font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Design Studio
              </Link>
              <Link to="/sale" className="block py-2 font-medium">
                {t('nav.sale')}
              </Link>
              <Link to="/contact" className="block py-2 font-medium">
                {t('nav.contact')}
              </Link>
            </div>

            <div className={`flex ${isRTL ? 'space-x-reverse' : ''} space-x-4 pt-4 border-t`}>
              <LanguageToggle />
              <Button variant="outline" className="flex-1" onClick={handleUserIconClick}>
                <User className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                {t('nav.account')}
              </Button>
              <Button variant="default" className="flex-1 relative" onClick={() => setCartDrawerOpen(true)}>
                <ShoppingCart className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                Cart ({cartCount})
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Auth Modal */}
      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />

      {/* Cart Drawer */}
      <CartDrawer open={cartDrawerOpen} onOpenChange={setCartDrawerOpen} />
    </nav>
  );
};

export default Navigation;
